import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Eye, Pencil, RefreshCw, Trash2, FileText, Plus, Loader2, BellRing } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, fmtDate, fmtBytes, fetchBlob, downloadFile } from "@/lib/api";
import { useApp } from "@/lib/app-context";
import { ProductIcon, Spinner, StatusBadge } from "@/components/ww/Misc";
import { CATEGORIES, ACCEPT, checkFile } from "./ProductForm";
import { CoverageTimeline } from "@/components/ww/CoverageTimeline";
import { CalendarExport } from "@/components/ww/CalendarExport";

const catLabel = (c) => CATEGORIES.find(([v]) => v === c)?.[1] || c;
const Info = ({ label, value, testid }) => (
  <div><p className="text-xs font-medium uppercase tracking-wider text-ww-muted">{label}</p><p className="mt-1 break-words font-semibold text-ww-navy" data-testid={testid}>{value || "—"}</p></div>
);

function DocRow({ d, onView, onDownload, onReplace, onDelete }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-ww-border p-4 sm:flex-row sm:items-center" data-testid={`document-row-${d.id}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ww-cyan text-ww-emerald"><FileText className="h-5 w-5" /></span>
        <div className="min-w-0"><p className="truncate text-sm font-semibold text-ww-navy">{d.filename}</p><p className="text-xs text-ww-muted">{catLabel(d.category)} · {fmtBytes(d.size)}</p></div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {[[Eye, "View", onView, "view"], [Download, "Download", onDownload, "download"], [RefreshCw, "Replace", onReplace, "replace"], [Trash2, "Delete", onDelete, "delete"]].map(([I, l, fn, k]) => (
          <button key={k} onClick={fn} className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${k === "delete" ? "text-red-600 hover:bg-red-50" : "text-ww-navy hover:bg-ww-mint"}`} data-testid={`document-${k}-${d.id}`}><I className="h-3.5 w-3.5" />{l}</button>
        ))}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { refreshMe } = useApp();
  const [p, setP] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState("invoice");
  const addRef = useRef();
  const replaceRef = useRef();
  const replaceId = useRef(null);

  const load = useCallback(() => api("get", `/products/${id}`).then(setP).catch(() => { toast.error("Product not found"); navigate("/wallet"); }), [api, id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => viewer && URL.revokeObjectURL(viewer.url), [viewer]);

  const upload = async (file, path, extra) => {
    const err = checkFile(file);
    if (err) return toast.error(err);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(extra || {}).forEach(([k, v]) => fd.append(k, v));
    try { await api("post", path, fd); toast.success("Document saved securely"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const view = async (d) => {
    try { setViewer({ url: await fetchBlob(api, `/documents/${d.id}/file`), type: d.content_type, name: d.filename }); }
    catch (e) { toast.error("Could not open document"); }
  };
  const doConfirm = async () => {
    try {
      if (confirm.kind === "product") { await api("delete", `/products/${id}`); refreshMe(); toast.success("Product deleted"); navigate("/wallet"); }
      else { await api("delete", `/documents/${confirm.doc.id}`); toast.success("Document deleted"); load(); }
    } catch (e) { toast.error(errMsg(e)); }
    setConfirm(null);
  };

  if (!p) return <Spinner />;
  const priceText = p.purchase_price != null ? new Intl.NumberFormat(undefined, { style: "currency", currency: p.price_currency || "INR" }).format(p.purchase_price) : null;
  return (
    <main className="mx-auto max-w-5xl px-5 py-12 lg:px-8" data-testid="product-detail-page">
      <button onClick={() => navigate("/wallet")} className="flex items-center gap-1.5 text-sm font-semibold text-ww-muted hover:text-ww-emerald" data-testid="back-to-wallet-button"><ArrowLeft className="h-4 w-4" />My Wallet</button>
      <div className="mt-6 flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
        <div className="flex items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><ProductIcon name={p.name} className="h-8 w-8" /></span>
          <div><h1 className="font-display text-3xl font-semibold tracking-tight text-ww-navy sm:text-4xl" data-testid="product-detail-name">{p.name}</h1><div className="mt-2"><StatusBadge status={p.status} testid="product-detail-status" /></div></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <CalendarExport productId={id} />
          <button onClick={() => navigate(`/products/${id}/edit`)} className="ww-btn-ghost" data-testid="edit-product-button"><Pencil className="h-4 w-4" />Edit Product</button>
          <button onClick={() => setConfirm({ kind: "product" })} className="ww-btn-ghost text-red-600 hover:border-red-200 hover:bg-red-50" data-testid="delete-product-button"><Trash2 className="h-4 w-4" />Delete</button>
        </div>
      </div>

      <section className="mt-8 grid gap-6 rounded-[26px] border border-ww-border bg-white p-6 sm:grid-cols-3 sm:p-8">
        <Info label="Brand" value={p.brand} testid="detail-brand" /><Info label="Model" value={p.model_number} testid="detail-model" /><Info label="Serial Number" value={p.serial_number} testid="detail-serial" />
        <Info label="Purchase Date" value={fmtDate(p.purchase_date)} testid="detail-purchase-date" /><Info label="Purchase Price" value={priceText} testid="detail-price" /><Info label="Seller" value={p.seller} testid="detail-seller" />
        {p.notes && <div className="sm:col-span-3"><Info label="Notes" value={p.notes} testid="detail-notes" /></div>}
      </section>

      <section className="mt-6 rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="detail-coverages">
        <h2 className="font-display text-xl font-semibold text-ww-navy">Coverage Periods</h2>
        <div className="mt-5 space-y-3">
          {p.coverages.map((c, i) => (
            <div key={c.id} className="flex flex-col gap-2 rounded-2xl bg-ww-cyan/50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`detail-coverage-${i}`}>
              <div><p className="font-semibold text-ww-navy">{c.name || (c.type === "warranty" ? "Warranty" : "Guarantee")} <span className="ml-1 text-xs font-medium uppercase text-ww-muted">{c.type}</span></p>
                <p className="text-sm text-ww-muted">{fmtDate(c.start_date)} – {fmtDate(c.expiry_date)}{c.notes ? ` · ${c.notes}` : ""}</p></div>
              <div className="flex items-center gap-3"><span className="text-xs font-medium text-ww-muted">{c.days_left >= 0 ? `${c.days_left} days left` : `Expired ${-c.days_left} days ago`}</span><StatusBadge status={c.status} testid={`detail-coverage-status-${i}`} /></div>
            </div>
          ))}
        </div>
        {p.reminders?.length > 0 && <p className="mt-4 flex items-center gap-2 text-xs text-ww-muted"><BellRing className="h-3.5 w-3.5 text-ww-emerald" />{p.reminders.filter((r) => r.status === "pending").length} upcoming reminders scheduled. Each coverage period is reminded 30 days, 7 days and 1 day before expiry, and on the expiry day.</p>}
      </section>

      <CoverageTimeline coverages={p.coverages} />

      <section className="mt-6 rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="detail-documents">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <h2 className="font-display text-xl font-semibold text-ww-navy">Documents <span className="text-sm font-medium text-ww-muted">({p.documents.length}/10)</span></h2>
          {p.documents.length < 10 && (
            <div className="flex gap-2">
              <Select value={newCat} onValueChange={setNewCat}><SelectTrigger className="h-10 w-48 rounded-xl" data-testid="add-document-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              <button className="ww-btn-primary" disabled={busy} onClick={() => addRef.current.click()} data-testid="add-document-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add</button>
            </div>
          )}
        </div>
        <input ref={addRef} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) upload(f, `/products/${id}/documents`, { category: newCat }); }} data-testid="add-document-input" />
        <input ref={replaceRef} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) upload(f, `/documents/${replaceId.current}/replace`); }} data-testid="replace-document-input" />
        <div className="mt-5 space-y-3">
          {p.documents.length === 0 && <p className="rounded-2xl bg-ww-mint/50 px-4 py-6 text-center text-sm text-ww-muted">No documents yet. Add your invoice, warranty or guarantee card.</p>}
          {p.documents.map((d) => (
            <DocRow key={d.id} d={d} onView={() => view(d)} onDownload={() => downloadFile(api, `/documents/${d.id}/file?download=1`, d.filename).catch(() => toast.error("Download failed"))}
              onReplace={() => { replaceId.current = d.id; replaceRef.current.click(); }} onDelete={() => setConfirm({ kind: "doc", doc: d })} />
          ))}
        </div>
      </section>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-[24px]" data-testid="confirm-delete-dialog">
          <AlertDialogTitle>{confirm?.kind === "product" ? "Delete this product?" : "Delete this document?"}</AlertDialogTitle>
          <AlertDialogDescription>{confirm?.kind === "product" ? "This permanently removes the product, its coverage periods, documents and scheduled reminders." : `"${confirm?.doc?.filename}" will be removed from this product.`}</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={doConfirm} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-h-[94vh] max-w-4xl rounded-[24px] p-4" data-testid="document-viewer">
          <DialogTitle className="truncate pr-8 text-base">{viewer?.name}</DialogTitle>
          <DialogDescription className="sr-only">Document preview</DialogDescription>
          {viewer?.type === "application/pdf" ? <iframe title="Document" src={viewer.url} className="h-[75vh] w-full rounded-xl" /> : viewer && <img src={viewer.url} alt={viewer.name} className="mx-auto max-h-[75vh] rounded-xl object-contain" />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
